import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import { FileText } from 'lucide-react';
import * as React from 'react';

import { PageHeader } from '@/design';
import { errorMessage } from '@/domains/api';
import { surveyApi } from '../api';
import type { IMyCycle, IMyCycleResponse, IMySubmission } from '../api/contracts';
import { formatDateTime } from '../format';
import { surveyTitle } from '../survey-labels';
import { responsesOf } from './catalogue';
import { SubmissionDownloadButton } from './submission-download';

/**
 * Documents — the PDF of each survey the advisor has submitted, to file with their own firm's
 * compliance department (SUR-10). survey-api renders each from what the advisor saw at submit,
 * never from staff's later corrections, and nothing in it is Veritant's own.
 *
 * The list is `GET /my/cycles`' responses that carry a submission, newest first — the one read
 * the page needs. A response's earlier submissions (AC2) are read only when asked for, one
 * response at a time, so the page costs one read however many surveys it lists.
 */
export function DocumentsPage() {
  const [documents, setDocuments] = React.useState<Nullable<IDocument[]>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);

  React.useEffect(() => {
    const controller = new AbortController();
    void surveyApi
      .myCycles(controller.signal)
      .then((cycles) => setDocuments(documentsOf(cycles)))
      .catch((loadError: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(loadError, 'Could not load your surveys'));
      });
    return () => controller.abort();
  }, []);

  return (
    <>
      <PageHeader
        title="Documents"
        lede="A copy of each survey you have submitted, as a PDF to keep or print. Each shows the questions and your answers as you submitted them — nothing Veritant adds."
      />

      <div className="mx-auto grid w-full max-w-[1120px] gap-6 px-6 pt-6 pb-16 lg:px-10">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {error ? null : documents === null ? (
          <Skeleton className="h-40 w-full" />
        ) : documents.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-muted-foreground">
              Nothing submitted yet. A survey appears here as soon as you submit it.
            </CardContent>
          </Card>
        ) : (
          <ul aria-label="Your submitted surveys" className="grid gap-4">
            {documents.map((document) => (
              <DocumentRow key={document.response.responseId} document={document} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

interface IDocument {
  cycle: IMyCycle;
  response: IMyCycleResponse;
  submittedAt: string;
}

function documentsOf(cycles: IMyCycle[]): IDocument[] {
  return responsesOf(cycles, null)
    .flatMap(({ cycle, response }) =>
      response.submittedAt ? [{ cycle, response, submittedAt: response.submittedAt }] : []
    )
    .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
}

function DocumentRow({ document }: { document: IDocument }) {
  const { cycle, response, submittedAt } = document;
  const name = `${surveyTitle(cycle.survey.code)} survey — ${cycle.cycle.code}`;
  const codes = { surveyCode: cycle.survey.code, cycleCode: cycle.cycle.code };

  return (
    <li className="grid gap-4 rounded-lg border border-border bg-white p-5">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
        <div className="flex min-w-0 items-start gap-4">
          <FileText className="mt-1 size-6 shrink-0 text-[var(--color-sapphire)]" aria-hidden />
          <div className="grid min-w-0 gap-2">
            <h2 className="text-lg font-medium text-[var(--color-royal-blue)]">{name}</h2>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-[0.9375rem]">
              <dt className="text-muted-foreground">For</dt>
              <dd>{response.participantName}</dd>
              <dt className="text-muted-foreground">Submitted</dt>
              <dd>{formatDateTime(submittedAt)}</dd>
              <dt className="text-muted-foreground">Your reference</dt>
              <dd className="font-mono text-[0.875rem] break-all">{response.responseId}</dd>
            </dl>
          </div>
        </div>
        <SubmissionDownloadButton
          responseId={response.responseId}
          {...codes}
          label="Download the PDF"
          context={`of the ${name}`}
        />
      </div>
      <EarlierVersions responseId={response.responseId} name={name} {...codes} />
    </li>
  );
}

/** Every submission of one response but the latest, read the first time the list is opened
 *  (AC2) — once, unless that read failed or the list was closed before it answered, when
 *  opening it again retries. */
function EarlierVersions({
  responseId,
  name,
  surveyCode,
  cycleCode
}: {
  responseId: string;
  name: string;
  surveyCode: string;
  cycleCode: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [submissions, setSubmissions] = React.useState<Nullable<IMySubmission[]>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);
  const listId = React.useId();

  React.useEffect(() => {
    if (!open || submissions !== null) return undefined;
    const controller = new AbortController();
    setError(null);
    surveyApi
      .mySubmissions(responseId, controller.signal)
      .then(setSubmissions)
      .catch((loadError: unknown) => {
        if (!controller.signal.aborted) {
          setError(errorMessage(loadError, 'Could not load the earlier versions'));
        }
      });
    return () => controller.abort();
  }, [open]);

  const earlier = submissions?.slice(1) ?? [];

  return (
    <div className="grid gap-2 border-t border-border pt-3">
      <Button
        type="button"
        variant="ghost"
        className="justify-self-start"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((was) => !was)}
      >
        {open ? 'Hide earlier versions' : 'Earlier versions'}
      </Button>
      {open && (
        <div id={listId} className="grid gap-2">
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : submissions === null ? (
            <Skeleton className="h-8 w-full" />
          ) : earlier.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              You submitted this survey once; the PDF above is the only version.
            </p>
          ) : (
            <ul aria-label={`Earlier versions of the ${name}`} className="grid gap-2">
              {earlier.map((submission) => (
                <li
                  key={submission.submittedAt}
                  className="flex flex-wrap items-center justify-between gap-3 text-[0.9375rem]"
                >
                  <span>Submitted {formatDateTime(submission.submittedAt)}</span>
                  <SubmissionDownloadButton
                    responseId={responseId}
                    submissionId={submission.submissionId}
                    surveyCode={surveyCode}
                    cycleCode={cycleCode}
                    label="Download"
                    context={`the version submitted ${formatDateTime(submission.submittedAt)}`}
                    variant="outline"
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
